<script lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import type { UseComponentIconsProps } from '../composables/useComponentIcons'
import type { Color } from '../styles/colors'
import { buttonGroupVariantUi } from './AwButtonGroup.vue'

export interface ButtonProps extends UseComponentIconsProps {
  label?: string
  color?: Color
  variant?: 'solid' | 'outline' | 'ghost' | 'link'
  size?: 'sm' | 'md' | 'lg'
  class?: string | object
  to?: RouteLocationRaw
  href?: string
  target?: '_blank' | '_parent' | '_self' | '_top' | (string & {}) | null
  ariaLabel?: string
  disabled?: boolean
  /** Pressed state for toggles and segmented groups. */
  isSelected?: boolean
  type?: string
  // Declared as `=> void` rather than `=> void | Promise<void>`: TypeScript only applies its
  // return-type-void assignability exception when the target return type is exactly `void`,
  // and a handler returning `Promise<void>` still satisfies it.
  onClick?: ((event: MouseEvent) => void) | Array<(event: MouseEvent) => void>
}

export interface ButtonSlots {
  leading?: (props?: object) => any
  default?: (props?: object) => any
  trailing?: (props?: object) => any
}

const ui = {
  base: 'cursor-pointer shadow-xs inline-flex items-center outline-none focus:outline-none focus-visible:outline-0 flex-shrink-0 transition-colors',
  disabled: 'disabled:cursor-not-allowed',
  font: 'font-semibold',
  rounded: 'rounded-2',
  size: {
    sm: 'text-sm',
    md: 'text-sm',
    lg: 'text-md',
  },
  gap: {
    sm: 'gap-x-1',
    md: 'gap-x-1',
    lg: 'gap-x-1',
  },
  padding: {
    sm: {
      base: 'px-2 py-1.5',
      icon: 'p-1.5',
    },
    md: {
      base: 'px-3 py-2',
      icon: 'p-1.5',
    },
    lg: {
      base: 'px-4 py-3',
      icon: 'p-2',
    },
  },
  scale: 'active:scale-97',
  variant: {
    solid: {
      default: 'text-white bg-{color}-500 hover:bg-{color}-600 active:bg-{color}-600 disabled:bg-secondary-50 disabled:text-secondary-600',
      secondary: 'text-white bg-secondary-900 hover:bg-black active:bg-black disabled:bg-secondary-50 disabled:text-secondary-600',
    },
    outline: {
      default: 'text-{color}-500 bg-white hover:bg-{color}-50 active::bg-{color}-50 disabled:bg-secondary-50 disabled:ring-0 disabled:hover:bg-secondary-50 disabled:active:bg-secondary-50 disabled:text-secondary-600 ring-inset ring-1 ring-secondary-400',
      secondary: 'text-secondary-900 bg-white hover:bg-secondary-50 active:bg-secondary-50 disabled:bg-secondary-50 disabled:ring-0 disabled:text-secondary-600 disabled:active:bg-secondary-50 disabled:text-secondary-600 ring-inset ring-1 ring-secondary-400',
    },
    ghost: {
      default: 'text-{color}-500 hover:bg-secondary-100 active:bg-secondary-100 disabled:text-secondary-600 disabled:bg-secondary-50 disabled:hover:bg-secondary-50',
      secondary: 'text-secondary-900 hover:bg-secondary-100 active:bg-secondary-100 disabled:bg-secondary-50 disabled:text-secondary-600',
    },
    link: {
      default: 'text-{color}-500 hover:text-{color}-600 active:text-{color}-600 underline-offset-4 hover:underline disabled:no-underline disabled:text-secondary-600',
      secondary: 'text-secondary-900 hover:text-black active:text-black underline-offset-4 hover:underline disabled:no-underline disabled:text-secondary-600',
      gray: 'text-gray-700 hover:text-gray-800 active:text-gray-700 underline-offset-4 hover:underline disabled:no-underline disabled:text-secondary-600',
    },
  },
  selected: 'text-{color}-600 bg-{color}-50 hover:bg-{color}-100 active:bg-{color}-100 ring-inset ring-1 ring-{color}-300',
  icon: {
    base: 'flex-shrink-0',
    size: {
      sm: {
        default: 'size-3',
        only: 'size-3.75',
      },
      md: {
        default: 'size-3',
        only: 'size-4.75',
      },
      lg: {
        default: 'size-4',
        only: 'size-5.75',
      },
    },
  },
  ...buttonGroupVariantUi,
}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { useButtonGroup } from '../composables/useButtonGroup'
import { useComponentIcons } from '../composables/useComponentIcons'
import AwIcon from './AwIcon.vue'

const props = withDefaults(defineProps<ButtonProps>(), {
  color: 'primary',
  variant: 'solid',
  type: 'button',
  isSelected: undefined,
})
const slots = defineSlots<ButtonSlots>()

const { isLeading, isTrailing, leadingIconName, trailingIconName } = useComponentIcons(computed(() => ({ ...props })))
const { hasButtonGroup, orientation, size: buttonGroupSize } = useButtonGroup<ButtonProps>(props)

const size = computed(() => props.size ?? 'md')
const isDisabled = computed(() => props.disabled || props.isLoading)
const component = computed(() => props.to ? 'RouterLink' : props.href ? 'a' : 'button')
const buttonProps = computed(() => {
  const defaultProps = { disabled: isDisabled.value, type: props.type }
  if (props.to) {
    return { ...defaultProps, to: props.to, target: props.target }
  }
  if (props.href) {
    return { ...defaultProps, href: props.href, target: props.target }
  }
  return defaultProps
})
const leadingIconClass = computed(() => [
  ui.icon.base,
  (props.label || slots.default) ? ui.icon.size[buttonGroupSize.value ?? size.value].default : ui.icon.size[buttonGroupSize.value ?? size.value].only,
  props.isLoading && 'animate-spin',
])
const trailingIconClass = computed(() => [
  ui.icon.base,
  (props.label || slots.default) ? ui.icon.size[buttonGroupSize.value ?? size.value].default : ui.icon.size[buttonGroupSize.value ?? size.value].only,
  props.isLoading && !isLeading.value && 'animate-spin',
])
const buttonClass = computed(() => [
  ui.base,
  ui.disabled,
  ui.font,
  ui.rounded,
  props.class,
  ui.size[buttonGroupSize.value ?? size.value],
  props.variant === 'link' ? 'p-0' : (props.label || slots.default) ? ui.padding[buttonGroupSize.value ?? size.value].base : ui.padding[buttonGroupSize.value ?? size.value].icon,
  ui.gap[buttonGroupSize.value ?? size.value],
  props.isSelected
    ? ui.selected.replaceAll('{color}', props.color)
    : props.color === 'secondary' ? ui.variant[props.variant].secondary : (props.color === 'gray' && props.variant === 'link') ? ui.variant[props.variant].gray : ui.variant[props.variant].default.replaceAll('{color}', props.color),
  hasButtonGroup.value && ui.buttonGroup[orientation.value],
  !hasButtonGroup.value && props.variant !== 'link' && ui.scale,
])
</script>

<template>
  <component
    :is="component"
    :class="buttonClass"
    :aria-label="ariaLabel"
    :aria-pressed="isSelected"
    v-bind="buttonProps"
    @click="onClick"
  >
    <slot name="leading" :disabled="isDisabled" :is-loading>
      <AwIcon v-if="isLeading && leadingIconName" :name="leadingIconName" :class="leadingIconClass" />
    </slot>
    <slot>
      <span v-if="label">
        {{ label }}
      </span>
    </slot>
    <slot name="trailing" :disabled="isDisabled" :is-loading>
      <AwIcon v-if="isTrailing && trailingIconName" :name="trailingIconName" :class="trailingIconClass" />
    </slot>
  </component>
</template>
